import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { createChart } from 'lightweight-charts';
import { 
  Activity, TrendingUp, TrendingDown, Wallet, RefreshCw, 
  ArrowUpRight, ArrowDownRight, Clock, DollarSign, Coins
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/api';
import { useTokenConfig } from '../contexts/TokenConfigContext';

const API_URL = getApiUrl();

// Format number with appropriate suffix
const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '0';
  if (Math.abs(num) >= 1e9) return (num / 1e9).toFixed(decimals) + 'B';
  if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(decimals) + 'M';
  if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(decimals) + 'K';
  return num.toFixed(decimals);
};

// Format percentage - only element that should be colored
const PercentBadge = ({ value }) => {
  if (value === null || value === undefined) return null;
  const isPositive = value > 0;
  const isZero = Math.abs(value) < 0.01;
  
  return (
    <span className={`inline-flex items-center text-sm font-medium ${
      isZero ? 'text-gray-500' : isPositive ? 'text-green-500' : 'text-red-500'
    }`}>
      {!isZero && (isPositive ? <ArrowUpRight className="w-3 h-3 mr-0.5" /> : <ArrowDownRight className="w-3 h-3 mr-0.5" />)}
      {isPositive ? '+' : ''}{value.toFixed(2)}%
    </span>
  );
};

// Balance Chart Component
const BalanceChart = ({ data, height = 320, tokenSymbol }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();
  const [showTon, setShowTon] = useState(true);
  const [showBuyt, setShowBuyt] = useState(false);
  const [showUsd, setShowUsd] = useState(true);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: height,
      layout: {
        background: { type: 'solid', color: '#111827' },
        textColor: '#9ca3af',
      },
      grid: {
        vertLines: { color: '#1f2937' },
        horzLines: { color: '#1f2937' },
      },
      rightPriceScale: {
        borderColor: '#374151',
      },
      timeScale: {
        borderColor: '#374151',
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: {
        mode: 1,
        vertLine: { color: '#4b5563', style: 2 },
        horzLine: { color: '#4b5563', style: 2 },
      },
    });

    chartRef.current = chart;

    const tonSeries = chart.addLineSeries({
      color: '#60a5fa',
      lineWidth: 2,
      title: 'TON',
    });

    const buytSeries = chart.addLineSeries({
      color: '#fbbf24',
      lineWidth: 2,
      title: tokenSymbol || 'TOKEN',
      priceScaleId: 'left',
    });

    const usdSeries = chart.addAreaSeries({
      topColor: 'rgba(74, 222, 128, 0.2)',
      bottomColor: 'rgba(74, 222, 128, 0.0)',
      lineColor: '#4ade80',
      lineWidth: 2,
      title: 'USD',
    });

    chart.priceScale('left').applyOptions({
      borderColor: '#374151',
      visible: showBuyt,
    });

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    chartRef.current.tonSeries = tonSeries;
    chartRef.current.buytSeries = buytSeries;
    chartRef.current.usdSeries = usdSeries;

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
      }
    };
  }, [height]);

  useEffect(() => {
    if (!data || data.length === 0) return;
    if (!chartRef.current) return;

    const { tonSeries, buytSeries, usdSeries } = chartRef.current;
    if (!tonSeries || !buytSeries || !usdSeries) return;

    const seenTimestamps = new Set();
    const tonData = [];
    const buytData = [];
    const usdData = [];

    data.forEach((snapshot) => {
      if (!snapshot || !snapshot.timestamp) return;
      
      let timestamp = Math.floor(new Date(snapshot.timestamp).getTime() / 1000);
      while (seenTimestamps.has(timestamp)) {
        timestamp += 1;
      }
      seenTimestamps.add(timestamp);

      if (snapshot.total_ton != null && !isNaN(snapshot.total_ton)) {
        tonData.push({ time: timestamp, value: Number(snapshot.total_ton) });
      }
      if (snapshot.total_buyt != null && !isNaN(snapshot.total_buyt) && snapshot.total_buyt >= 0) {
        buytData.push({ time: timestamp, value: Number(snapshot.total_buyt) });
      }
      if (snapshot.total_usd != null && !isNaN(snapshot.total_usd)) {
        usdData.push({ time: timestamp, value: Number(snapshot.total_usd) });
      }
    });

    const sortByTime = (a, b) => a.time - b.time;
    tonData.sort(sortByTime);
    buytData.sort(sortByTime);
    usdData.sort(sortByTime);

    tonSeries.setData(tonData);
    buytSeries.setData(buytData);
    usdSeries.setData(usdData);

    tonSeries.applyOptions({ visible: showTon });
    buytSeries.applyOptions({ visible: showBuyt });
    usdSeries.applyOptions({ visible: showUsd });

    if (chartRef.current) {
      chartRef.current.timeScale().fitContent();
      chartRef.current.priceScale('left').applyOptions({ visible: showBuyt });
    }
  }, [data, showTon, showBuyt, showUsd]);

  return (
    <div className="space-y-3">
      <div ref={chartContainerRef} className="rounded-lg overflow-hidden" />
      <div className="flex flex-wrap justify-center gap-6 text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showTon}
            onChange={(e) => setShowTon(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <span className="w-4 h-0.5 bg-blue-400 rounded"></span>
          <span className="text-gray-300">TON</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showBuyt}
            onChange={(e) => setShowBuyt(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <span className="w-4 h-0.5 bg-yellow-400 rounded"></span>
          <span className="text-gray-300">{tokenSymbol || 'TOKEN'}</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showUsd}
            onChange={(e) => setShowUsd(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <span className="w-4 h-0.5 bg-green-400 rounded"></span>
          <span className="text-gray-300">USD</span>
        </label>
      </div>
    </div>
  );
};

// Stat Card - Clean minimal design
const StatCard = ({ title, value, subtitle, trend }) => {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <p className="text-gray-500 text-sm mb-1">{title}</p>
      <p className="text-white text-xl font-semibold mb-1">{value}</p>
      <div className="flex items-center justify-between">
        <span className="text-gray-500 text-sm">{subtitle}</span>
        {trend !== undefined && <PercentBadge value={trend} />}
      </div>
    </div>
  );
};

// Balance Changes List - Minimal colors
const BalanceChangesList = ({ changes, tokenSymbol }) => {
  if (!changes || changes.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>Нет изменений</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-72 overflow-y-auto">
      {changes.map((change, idx) => (
        <div key={idx} className="flex items-center justify-between p-3 bg-gray-900 border border-gray-800 rounded-lg">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${change.change_type === 'increase' ? 'bg-green-500' : 'bg-red-500'}`} />
            <div>
              <p className="text-gray-300 text-sm font-mono">
                {change.wallet_address.slice(0, 8)}...{change.wallet_address.slice(-4)}
              </p>
              <p className="text-gray-600 text-xs">
                {new Date(change.timestamp).toLocaleString('ru-RU', { 
                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                })}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-sm font-medium ${change.change_type === 'increase' ? 'text-green-500' : 'text-red-500'}`}>
              {change.change_type === 'increase' ? '+' : ''}{formatNumber(change.delta)} {change.token}
            </p>
            <p className="text-gray-600 text-xs">${Math.abs(change.delta_usd).toFixed(2)}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Top Wallets Table - All white/gray text, no color coding
const TopWalletsTable = ({ wallets, tokenSymbol }) => {
  if (!wallets || wallets.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Wallet className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>Нет данных</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-800">
            <th className="text-left py-2 px-2 text-gray-500 font-normal">#</th>
            <th className="text-left py-2 px-2 text-gray-500 font-normal">Кошелек</th>
            <th className="text-right py-2 px-2 text-gray-500 font-normal">TON</th>
            <th className="text-right py-2 px-2 text-gray-500 font-normal">{tokenSymbol || 'TOKEN'}</th>
            <th className="text-right py-2 px-2 text-gray-500 font-normal">USD</th>
          </tr>
        </thead>
        <tbody>
          {wallets.map((wallet, idx) => (
            <tr key={wallet.address} className="border-b border-gray-800/50">
              <td className="py-2 px-2 text-gray-600">{idx + 1}</td>
              <td className="py-2 px-2">
                <span className="text-gray-300 font-mono text-xs">
                  {wallet.address.slice(0, 8)}...{wallet.address.slice(-4)}
                </span>
              </td>
              <td className="py-2 px-2 text-right text-gray-300">{formatNumber(wallet.ton_balance, 2)}</td>
              <td className="py-2 px-2 text-right text-gray-300">{formatNumber(wallet.buyt_balance)}</td>
              <td className="py-2 px-2 text-right text-white font-medium">${formatNumber(wallet.total_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Main Component
const BalanceAnalytics = () => {
  const { tokenSymbol } = useTokenConfig();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentBalances, setCurrentBalances] = useState(null);
  const [trackingStatus, setTrackingStatus] = useState({ running: false, snapshot_count: 0 });
  const [timeRange, setTimeRange] = useState(24);

  const loadData = useCallback(async () => {
    try {
      const [analyticsRes, historyRes, currentRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/wallets/balance-tracking/analytics`, { params: { hours: timeRange } }),
        axios.get(`${API_URL}/api/wallets/balance-tracking/history`, { params: { hours: timeRange } }),
        axios.get(`${API_URL}/api/wallets/balance-tracking/current`),
        axios.get(`${API_URL}/api/wallets/balance-tracking/status`)
      ]);

      setAnalytics(analyticsRes.data);
      setHistory(historyRes.data.history || []);
      setCurrentBalances(currentRes.data);
      setTrackingStatus(statusRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API_URL}/api/wallets/balance-tracking/snapshot`);
      toast.success('Снапшот создан');
      await loadData();
    } catch (error) {
      toast.error('Ошибка');
    } finally {
      setRefreshing(false);
    }
  };

  const toggleTracking = async () => {
    try {
      if (trackingStatus.running) {
        await axios.post(`${API_URL}/api/wallets/balance-tracking/stop`);
        toast.success('Остановлено');
      } else {
        await axios.post(`${API_URL}/api/wallets/balance-tracking/start`);
        toast.success('Запущено');
      }
      await loadData();
    } catch (error) {
      toast.error('Ошибка');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  const current = currentBalances || analytics?.current;
  const changes = analytics?.changes;

  return (
    <div className="space-y-4 p-4 bg-gray-950 min-h-screen">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            trackingStatus.running ? 'bg-green-900 text-green-400' : 'bg-gray-800 text-gray-500'
          }`}>
            {trackingStatus.running ? '● Активно' : '○ Стоп'}
          </span>
          <span className="text-gray-600 text-sm">{trackingStatus.snapshot_count} снапшотов</span>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {[6, 12, 24, 48, 168, 720].map((hours) => (
            <button
              key={hours}
              onClick={() => setTimeRange(hours)}
              className={`px-3 py-1 rounded text-xs ${
                timeRange === hours 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {hours < 24 ? `${hours}ч` : `${hours / 24}д`}
            </button>
          ))}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-3 py-1 rounded text-xs bg-gray-800 text-gray-400 hover:bg-gray-700 flex items-center gap-1"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            Снапшот
          </button>
          <button
            onClick={toggleTracking}
            className={`px-3 py-1 rounded text-xs ${
              trackingStatus.running 
                ? 'bg-red-900 text-red-400 hover:bg-red-800' 
                : 'bg-green-900 text-green-400 hover:bg-green-800'
            }`}
          >
            {trackingStatus.running ? 'Стоп' : 'Старт'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="TON"
          value={`${formatNumber(current?.total_ton || 0, 2)}`}
          subtitle={`$${formatNumber((current?.total_ton || 0) * (current?.ton_price_usd || 0))}`}
          trend={changes?.ton_percent}
        />
        <StatCard
          title={tokenSymbol || 'TOKEN'}
          value={`${formatNumber(current?.total_buyt || 0)}`}
          subtitle={`$${formatNumber((current?.total_buyt || 0) * (current?.buyt_price_usd || 0))}`}
          trend={changes?.buyt_percent}
        />
        <StatCard
          title="Стоимость"
          value={`$${formatNumber(current?.total_usd || 0)}`}
          subtitle={`${current?.wallet_count || 0} кошельков`}
          trend={changes?.usd_percent}
        />
        <StatCard
          title="Цена TON"
          value={`$${(current?.ton_price_usd || 0).toFixed(2)}`}
          subtitle={`${tokenSymbol || 'TOKEN'}: $${(current?.buyt_price_usd || 0).toFixed(6)}`}
        />
      </div>

      {/* Chart */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <h3 className="text-gray-300 text-sm font-medium mb-3">История балансов</h3>
        {history.length > 0 ? (
          <BalanceChart data={history} height={300} tokenSymbol={tokenSymbol} />
        ) : (
          <div className="flex items-center justify-center h-64 text-gray-600">
            <p>Нет данных</p>
          </div>
        )}
      </div>

      {/* Bottom */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-gray-300 text-sm font-medium mb-3">Последние изменения</h3>
          <BalanceChangesList changes={analytics?.recent_balance_changes} tokenSymbol={tokenSymbol} />
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-gray-300 text-sm font-medium mb-3">Топ кошельков</h3>
          <TopWalletsTable wallets={analytics?.top_wallets || currentBalances?.wallets?.slice(0, 10)} tokenSymbol={tokenSymbol} />
        </div>
      </div>

      {/* Footer */}
      {changes && (
        <div className="text-center text-xs text-gray-600 py-2 border-t border-gray-800">
          Период: {new Date(changes.period_start).toLocaleString('ru-RU', { 
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
          })} — {new Date(changes.period_end).toLocaleString('ru-RU', { 
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
          })}
          {' | '}
          <span className={changes.ton_delta >= 0 ? 'text-green-500' : 'text-red-500'}>
            TON: {changes.ton_delta >= 0 ? '+' : ''}{formatNumber(changes.ton_delta, 2)}
          </span>
          {' | '}
          <span className={changes.buyt_delta >= 0 ? 'text-green-500' : 'text-red-500'}>
            {tokenSymbol || 'TOKEN'}: {changes.buyt_delta >= 0 ? '+' : ''}{formatNumber(changes.buyt_delta)}
          </span>
          {' | '}
          <span className={changes.usd_delta >= 0 ? 'text-green-500' : 'text-red-500'}>
            USD: {changes.usd_delta >= 0 ? '+$' : '-$'}{formatNumber(Math.abs(changes.usd_delta))}
          </span>
        </div>
      )}
    </div>
  );
};

export default BalanceAnalytics;
